import { RogItemStatus } from '../types';

export { RogItemStatus };
export type RogItemStatusValue = `${RogItemStatus}`;

const API_DL_URL = process.env.REACT_APP_API_DL_URL!;

const toCamelCase = (str: string): string => str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const toSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const keysToCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(keysToCamelCase);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toCamelCase(k), keysToCamelCase(v)])
    );
  }
  return obj;
};

const keysToSnakeCase = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(keysToSnakeCase);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [toSnakeCase(k), keysToSnakeCase(v)])
    );
  }
  return obj;
};

let noAuthCallback: (() => void) | null = null;

export function setGtNoAuthCallback(callback: () => void): void {
  noAuthCallback = callback;
}

// --- Enums ---

export type DatasetStatusEnum = 'draft' | 'locked';
export type LabelSourceEnum = 'human' | 'verified_inference';
export type RunTypeEnum = 'OD' | 'PR' | 'PC' | 'Full';
export type ModelTypeEnum = 'OD' | 'PR' | 'PC';
export type DeploymentStatusEnum = 'live' | 'staging' | 'archived';
export type RunStatusEnum =
  | 'pending'
  | 'ray_running'
  | 'ray_completed'
  | 'pc_running'
  | 'completed'
  | 'outdated'
  | 'failed'
  | 'cancelled'
  | 'timed_out';

// --- PC inference ---

export type PogResultStatus = 'compliant' | 'non_compliant' | 'out' | 'faceover';

export type RogPositionResult = {
  final_item_number: string | null;
  rog_item_status: RogItemStatusValue[];
  list_of_pr_uuids: string[];
};

export type PcInference = {
  image_id: number;
  created_at: string;
  pog_results_by_ili: Record<string, PogResultStatus>;
  rog_results_by_position: Record<string, RogPositionResult>;
};

// --- Request types ---

export type CreateDatasetRequest = {
  dataset_name: string;
  description?: string;
  tags?: string[];
};

export type PatchDatasetRequest = {
  dataset_name?: string;
  description?: string;
  tags?: string[];
};

export type AddInferencesRequest = {
  gt_sample_ids: string[];
};

export type CreateGtSampleRequest = {
  image_id?: number;
  store_id?: number;
  camera_id?: number;
  captured_at?: string;
  label_source: LabelSourceEnum;
  source_inference_id?: string;
  pr_inference?: object;
  pc_inference?: PcInference;
};

export type CreateGtSampleFromInferenceRequest = {
  inference_id: string;
};

export type BulkCreateGtSampleFromInferenceRequest = {
  inference_ids: string[];
};

export type BulkCreateGtSampleFromInferenceResponse = {
  created: string[];
  skipped: string[];
};

export type CorrectGtSampleRequest = {
  field: string;
  value?: any;
  note?: string;
};

export type UpdatePcInferenceRequest = {
  pc_inference: PcInference;
  note?: string;
};

export type UpdatePrInferenceRequest = {
  pr_inference: object;
  note?: string;
};

export type PatchModelRequest = {
  description?: string;
  metadata?: object;
};

export type CreatePcModelRequest = {
  service_model_id: string;
  version: string;
  description?: string;
  deployment_status?: DeploymentStatusEnum;
  config?: object;
};

export type PatchRunCommentRequest = {
  comment?: string;
};

export type ModelIds = {
  OD?: string;
  PR?: string;
  PC?: string;
};

export type ScheduleRunRequest = {
  run_type: RunTypeEnum;
  model_ids: ModelIds;
  dataset_id: string;
  source_run_id?: string;
  config?: object;
};

export type CancelRunRequest = {
  reason?: string;
};

// --- Response types ---

export type DatasetListItem = {
  dataset_id?: string;
  dataset_name: string;
  description?: string;
  status: DatasetStatusEnum;
  sample_count: number;
  tags: string[];
  created_by?: string;
  created_at: string;
  locked_at?: string;
  last_sample_updated_at?: string;
};

export type DatasetListResponse = {
  items: DatasetListItem[];
  total: number;
};

export type DatasetDetailResponse = {
  dataset_id?: string;
  dataset_name: string;
  description?: string;
  status: DatasetStatusEnum;
  sample_count: number;
  tags: string[];
  created_by?: string;
  created_at: string;
  locked_at?: string;
  last_sample_updated_at?: string;
  gt_sample_ids: string[];
  locked_by?: string;
};

export type AddInferencesResponse = {
  sample_count: number;
};

export type GtSampleListItem = {
  gt_sample_id?: string;
  image_id?: number;
  image_url?: string;
  store_id?: number;
  chain_id?: number;
  camera_id?: number;
  dataset_id?: string;
  label_source: LabelSourceEnum;
  annotated_by?: string;
  annotated_at?: string;
  ingested_at: string;
  ingested_by?: string;
  has_pr_inference: boolean;
  has_pc_run_snapshot: boolean;
};

export type GtSampleListResponse = {
  items: GtSampleListItem[];
  total: number;
};

export type GtSampleResponse = {
  gtSampleId?: string;
  imageId?: number;
  imageUrl?: string;
  storeId?: number;
  chainId?: number;
  cameraId?: number;
  datasetId?: string;
  labelSource: LabelSourceEnum;
  annotatedBy?: string;
  annotatedAt?: string;
  ingestedAt: string;
  ingestedBy?: string;
  hasPrInference: boolean;
  hasPcRunSnapshot: boolean;
  prInference?: object;
  pcInference?: object;
  corrections: object[];
  capturedAt?: string;
  createdAt: string;
};

export type CreateGtSampleFromInferenceResponse = {
  gt_sample_id: string;
};

export type ScoreHistoryItem = {
  run_id: string;
  run_type: string;
  status: string;
  model_ids: object;
  triggered_at: string;
  metrics: object;
};

export type ScoreHistoryResponse = {
  gt_sample_id: string;
  total: number;
  items: ScoreHistoryItem[];
};

export type MetricsModelVersions = {
  OD?: string;
  PR?: string;
  PC?: string;
};

export type MetricsRow = {
  run_id: string;
  run_type: RunTypeEnum;
  triggered_at: string;
  triggered_by: string;
  model_versions: MetricsModelVersions;
  result?: object;
};

export type MetricsResponse = {
  dataset_id: string;
  dataset_name: string;
  total: number;
  rows: MetricsRow[];
};

export type EvalModelListItem = {
  model_id?: string;
  service_model_id: string;
  model_type: ModelTypeEnum;
  version: string;
  description?: string;
  deployment_status: DeploymentStatusEnum;
  is_default: boolean;
  embedding_count?: number;
  config?: object;
  registered_at: string;
  updated_at?: string;
};

export type ModelListResponse = {
  items: EvalModelListItem[];
  total: number;
};

export type EvalModelResponse = {
  model_id?: string;
  service_model_id: string;
  model_type: ModelTypeEnum;
  version: string;
  description?: string;
  deployment_status: DeploymentStatusEnum;
  is_default: boolean;
  embedding_count?: number;
  config?: object;
  registered_at: string;
  updated_at?: string;
  registered_by?: string;
  metadata: object;
};

export type RunListItem = {
  run_id?: string;
  run_type: RunTypeEnum;
  status: RunStatusEnum;
  model_ids: object;
  dataset_id: string;
  dataset_name?: string;
  source_run_id?: string;
  triggered_by: string;
  triggered_at: string;
  started_at?: string;
  completed_at?: string;
  result?: object;
  is_outdated?: boolean;
  inferences_expired: boolean;
  comment?: string;
};

export type RunListResponse = {
  items: RunListItem[];
  total: number;
};

export type RunDetailResponse = {
  run_id?: string;
  run_type: RunTypeEnum;
  status: RunStatusEnum;
  model_ids: object;
  dataset_id: string;
  dataset_name?: string;
  source_run_id?: string;
  triggered_by: string;
  triggered_at: string;
  started_at?: string;
  completed_at?: string;
  result?: object;
  is_outdated?: boolean;
  inferences_expired: boolean;
  comment?: string;
  config: object;
  reason?: string;
  can_edit_comment: boolean;
};

export type RunInferenceItem = {
  run_inference_id?: string;
  run_id: string;
  gt_sample_id: string;
  pr_inference?: object;
  pc_inference?: PcInference;
  metrics?: object;
  created_at: string;
};

export type RunInferencesProgress = {
  completed: number;
  total: number;
};

export type RunInferencesResponse = {
  items: RunInferenceItem[];
  total: number;
  run_status: RunStatusEnum;
  progress: RunInferencesProgress;
};

export type RunInferenceDetailResponse = {
  run_inference_id?: string;
  run_id: string;
  gt_sample: object;
  pr_inference?: object;
  pc_inference?: PcInference;
  metrics: object;
  run_to_gt_assignment?: { [key: string]: { [key: string]: string } };
};

export type PrInferenceListItem = {
  inference_id: string;
  chain_id?: number;
  store_id?: number;
  camera_id?: number;
  image_id?: number;
  products_count: number;
  cartons_count: number;
  mods_count: number;
  price_tags_count: number;
  created_at: string;
};

export type PrInferenceListResponse = {
  items: PrInferenceListItem[];
  has_next: boolean;
};

// --- Service ---

export class GtApiService {
  private static handleError(response: Response) {
    if (response.status === 401) {
      if (noAuthCallback) {
        noAuthCallback();
      }
    }
  }

  // ========== Datasets ==========

  static async listDatasets(
    token: string,
    params?: { status?: DatasetStatusEnum; skip?: number; limit?: number }
  ): Promise<DatasetListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/datasets?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing datasets');
    return response.json();
  }

  static async createDataset(
    token: string,
    request: CreateDatasetRequest
  ): Promise<DatasetDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error creating dataset');
    return response.json();
  }

  static async getDataset(token: string, datasetId: string): Promise<DatasetDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/${datasetId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting dataset');
    return response.json();
  }

  static async patchDataset(
    token: string,
    datasetId: string,
    request: PatchDatasetRequest
  ): Promise<DatasetDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/${datasetId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error patching dataset');
    return response.json();
  }

  static async addInferencesToDataset(
    token: string,
    datasetId: string,
    request: AddInferencesRequest
  ): Promise<AddInferencesResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/${datasetId}/samples`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error adding samples to dataset');
    return response.json();
  }

  static async removeInferenceFromDataset(
    token: string,
    datasetId: string,
    gtSampleId: string
  ): Promise<void> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/datasets/${datasetId}/samples/${gtSampleId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error removing sample from dataset');
  }

  static async lockDataset(token: string, datasetId: string): Promise<DatasetDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/${datasetId}/lock`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error locking dataset');
    return response.json();
  }

  static async listDatasetTags(token: string): Promise<string[]> {
    const response = await fetch(`${API_DL_URL}/v1/eval/datasets/tags`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing dataset tags');
    return response.json();
  }

  // ========== GT Samples ==========

  static async listGtSamples(
    token: string,
    params?: {
      datasetId?: string;
      unassigned?: boolean;
      storeId?: number;
      chainId?: number;
      ingestedBy?: string;
      labelSource?: LabelSourceEnum;
      imageId?: number;
      skip?: number;
      limit?: number;
    }
  ): Promise<GtSampleListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.datasetId !== undefined) searchParams.append('dataset_id', params.datasetId);
    if (params?.unassigned !== undefined)
      searchParams.append('unassigned', String(params.unassigned));
    if (params?.storeId !== undefined) searchParams.append('store_id', String(params.storeId));
    if (params?.chainId !== undefined) searchParams.append('chain_id', String(params.chainId));
    if (params?.ingestedBy !== undefined) searchParams.append('ingested_by', params.ingestedBy);
    if (params?.labelSource) searchParams.append('label_source', params.labelSource);
    if (params?.imageId !== undefined) searchParams.append('image_id', String(params.imageId));
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing GT samples');
    return response.json();
  }

  static async createGtSample(
    token: string,
    request: CreateGtSampleRequest
  ): Promise<GtSampleResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error creating GT sample');
    return response.json();
  }

  static async createGtSampleFromInference(
    token: string,
    request: CreateGtSampleFromInferenceRequest
  ): Promise<CreateGtSampleFromInferenceResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/from-inference`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('GT sample already exists');
      }

      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || 'Error creating GT sample from inference');
    }
    return response.json();
  }

  static async createGtSamplesFromInferences(
    token: string,
    request: BulkCreateGtSampleFromInferenceRequest
  ): Promise<BulkCreateGtSampleFromInferenceResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/from-inferences`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || 'Error creating GT samples from inferences');
    }
    return response.json();
  }

  static async getGtSample(token: string, gtSampleId: string): Promise<GtSampleResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/${gtSampleId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting GT sample');
    const data = await response.json();
    return keysToCamelCase(data);
  }

  static async correctGtSample(
    token: string,
    gtSampleId: string,
    request: CorrectGtSampleRequest
  ): Promise<GtSampleResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/${gtSampleId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error correcting GT sample');
    return response.json();
  }

  static async updateGtSamplePc(
    token: string,
    gtSampleId: string,
    request: UpdatePcInferenceRequest
  ): Promise<GtSampleResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/${gtSampleId}/pc-inference`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error updating GT sample PC');
    return response.json();
  }

  static async updateGtSamplePr(
    token: string,
    gtSampleId: string,
    request: UpdatePrInferenceRequest
  ): Promise<GtSampleResponse> {
    // Remove cartonBoxes and productBoxes - they are artificially created only for UI
    const { cartonBoxes, productBoxes, ...cleanedPrInference } = request.pr_inference as any;
    const cleanedRequest = { ...request, pr_inference: cleanedPrInference };

    const response = await fetch(`${API_DL_URL}/v1/eval/gt-samples/${gtSampleId}/pr-inference`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(cleanedRequest)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error updating GT sample PR');
    return response.json();
  }

  static async getScoreHistory(
    token: string,
    gtSampleId: string,
    params?: { skip?: number; limit?: number }
  ): Promise<ScoreHistoryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(
      `${API_DL_URL}/v1/eval/gt-samples/${gtSampleId}/runs?${searchParams.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting score history');
    return response.json();
  }

  // ========== Metrics ==========

  static async getMetrics(
    token: string,
    params: { datasetId: string; runType?: RunTypeEnum; skip?: number; limit?: number }
  ): Promise<MetricsResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append('dataset_id', params.datasetId);
    if (params.runType) searchParams.append('run_type', params.runType);
    if (params.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/metrics?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting metrics');
    return response.json();
  }

  // ========== Models ==========

  static async listModels(
    token: string,
    params?: {
      modelType?: ModelTypeEnum;
      deploymentStatus?: DeploymentStatusEnum;
      skip?: number;
      limit?: number;
    }
  ): Promise<ModelListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.modelType) searchParams.append('model_type', params.modelType);
    if (params?.deploymentStatus) searchParams.append('deployment_status', params.deploymentStatus);
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/models?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing models');
    return response.json();
  }

  static async createPcModel(
    token: string,
    request: CreatePcModelRequest
  ): Promise<EvalModelResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/pc`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error creating PC model');
    return response.json();
  }

  static async syncModels(token: string): Promise<ModelListResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/sync`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error syncing models');
    return response.json();
  }

  static async updateEmbeddingCounts(token: string): Promise<ModelListResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/update-embedding-counts`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error updating embedding counts');
    return response.json();
  }

  static async getModel(token: string, modelId: string): Promise<EvalModelResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/${modelId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting model');
    return response.json();
  }

  static async patchModel(
    token: string,
    modelId: string,
    request: PatchModelRequest
  ): Promise<EvalModelResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/models/${modelId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error patching model');
    return response.json();
  }

  // ========== PR Inferences ==========

  static async listPrInferences(
    token: string,
    params?: {
      storeId?: number;
      chainId?: number;
      cameraId?: number;
      skip?: number;
      limit?: number;
    }
  ): Promise<PrInferenceListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.storeId !== undefined) searchParams.append('store_id', String(params.storeId));
    if (params?.chainId !== undefined) searchParams.append('chain_id', String(params.chainId));
    if (params?.cameraId !== undefined) searchParams.append('camera_id', String(params.cameraId));
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/pr-inferences?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing PR inferences');
    return response.json();
  }

  // ========== Runs ==========

  static async listRuns(
    token: string,
    params?: {
      datasetId?: string;
      modelId?: string;
      status?: RunStatusEnum;
      runType?: RunTypeEnum;
      skip?: number;
      limit?: number;
    }
  ): Promise<RunListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.datasetId) searchParams.append('dataset_id', params.datasetId);
    if (params?.modelId) searchParams.append('model_id', params.modelId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.runType) searchParams.append('run_type', params.runType);
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(`${API_DL_URL}/v1/eval/runs?${searchParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing runs');
    return response.json();
  }

  static async scheduleRun(token: string, request: ScheduleRunRequest): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error scheduling run');
    return response.json();
  }

  static async getRun(token: string, runId: string): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting run');
    return response.json();
  }

  static async deleteRun(token: string, runId: string): Promise<void> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error deleting run');
  }

  static async patchRunComment(
    token: string,
    runId: string,
    request: PatchRunCommentRequest
  ): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}/comment`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error patching run comment');
    return response.json();
  }

  static async cancelRun(
    token: string,
    runId: string,
    request: CancelRunRequest
  ): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}/cancel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(keysToSnakeCase(request)),
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error cancelling run');
    return response.json();
  }

  static async listRunInferences(
    token: string,
    runId: string,
    params?: { skip?: number; limit?: number }
  ): Promise<RunInferencesResponse> {
    const searchParams = new URLSearchParams();
    if (params?.skip !== undefined) searchParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) searchParams.append('limit', String(params.limit));

    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${runId}/inferences?${searchParams.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error listing run inferences');
    return response.json();
  }

  static async getRunInference(
    token: string,
    runId: string,
    runInferenceId: string
  ): Promise<RunInferenceDetailResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/eval/runs/${runId}/inferences/${runInferenceId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error getting run inference');
    return response.json();
  }

  static async retryRun(token: string, runId: string): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}/retry`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error retrying run');
    return response.json();
  }

  static async timeoutRun(token: string, runId: string): Promise<RunDetailResponse> {
    const response = await fetch(`${API_DL_URL}/v1/eval/runs/${runId}/timeout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    GtApiService.handleError(response);
    if (!response.ok) throw new Error('Error timing out run');
    return response.json();
  }
}
